import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BadgeCheck, Clock, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { upgradeAPI } from '../services/api';

/**
 * "Already purchased?" card shown on /pro-access for logged-in free users.
 * Lets them submit their Linktree order email so admins can approve the
 * upgrade from the Pending Upgrades queue.
 */
const UpgradeRequestCard = ({ userEmail }) => {
  const [orderEmail, setOrderEmail] = useState(userEmail || '');
  const [note, setNote] = useState('');
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    upgradeAPI
      .me()
      .then((d) => setExisting(d.request))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const req = await upgradeAPI.submit(orderEmail.trim(), note.trim() || null);
      setExisting(req);
      toast.success('Request submitted — we process upgrades within 24 hours');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit your request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const status = existing?.status;

  return (
    <div
      className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6"
      data-testid="upgrade-request-card"
    >
      <h3 className="text-base font-semibold text-white mb-1">Already purchased?</h3>
      <p className="text-sm text-white/55 mb-4">
        Submit the email you used on the Linktree checkout and we&apos;ll match your order —
        your account is upgraded to Pro within 24 hours.
      </p>

      {status === 'pending' && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-amber-300 text-sm" data-testid="upgrade-status-pending">
          <Clock size={16} />
          Request received ({existing.order_email}) — being processed, check back within 24 hours.
        </div>
      )}
      {status === 'approved' && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-emerald-300 text-sm" data-testid="upgrade-status-approved">
          <BadgeCheck size={16} />
          Approved! Log out and back in if Pro features haven&apos;t unlocked yet.
        </div>
      )}
      {status === 'rejected' && (
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-red-300 text-sm mb-4" data-testid="upgrade-status-rejected">
          <p className="flex items-center gap-2"><XCircle size={16} /> Your last request couldn&apos;t be verified{existing.reject_reason ? ` — ${existing.reject_reason}` : ''}. You can submit again.</p>
        </div>
      )}

      {(!status || status === 'rejected') && (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            required
            value={orderEmail}
            onChange={(e) => setOrderEmail(e.target.value)}
            placeholder="Email used at checkout"
            className="flex-1 bg-white/5 border-white/15 text-white placeholder:text-white/30"
            data-testid="upgrade-order-email-input"
          />
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Order # or note (optional)"
            maxLength={500}
            className="flex-1 bg-white/5 border-white/15 text-white placeholder:text-white/30"
            data-testid="upgrade-note-input"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-400 hover:to-violet-400 text-white font-semibold"
            data-testid="upgrade-submit-btn"
          >
            <Send size={14} className="mr-1.5" />
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default UpgradeRequestCard;
